/**
 * Holding the UI adjustment key puts the interface into a mode where it can be
 * rearranged. Alt, unless somebody has bound something else.
 *
 * Some of what is on the screen is only there when something is happening: the
 * notices that pile up, the numbers that float when experience is earned. There
 * is no way to take hold of something that is not there, so holding the key
 * shows a ghost of every one of those where it sits, ready to be dragged, and
 * reveals the grips that resize the windows and gauges that have them.
 *
 * A key held rather than a mode switched, because rearranging an interface is a
 * thing somebody does for ten seconds and then stops doing.
 */

import { useEffect, useState } from 'react';
import { bareKeyOf, loadKeys } from './keys';

/** The key that is held, read each time so a new binding takes at once. */
function adjustKey(): string {
  const bound = loadKeys()['ui.adjust'] ?? 'Alt';
  // Written by an older capture as the modifier twice over.
  return bound === 'Alt+Alt' ? 'Alt' : bound;
}

/** Whether a key event is the UI adjustment key, pressed or let go. */
function isAdjustKey(event: KeyboardEvent): boolean {
  const key = adjustKey();
  return key !== '' && bareKeyOf(event) === key;
}

export function useAltHeld(): boolean {
  const [held, setHeld] = useState(false);

  useEffect(() => {
    const down = (event: KeyboardEvent): void => {
      if (isAdjustKey(event)) setHeld(true);
    };

    const up = (event: KeyboardEvent): void => {
      if (isAdjustKey(event)) setHeld(false);
    };

    // Letting go outside the page, or alt-tabbing away, has to end it as well:
    // a key released where nobody is listening is a key that stays held forever.
    const away = (): void => setHeld(false);

    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', away);

    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', away);
    };
  }, []);

  return held;
}
